import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ClipboardCheck, Search, FileText, ShieldCheck, ArrowRight } from 'lucide-react';
import { processSteps } from '../mock';

const iconMap = {
  'clipboard-check': ClipboardCheck,
  'search': Search,
  'file-text': FileText,
  'shield-check': ShieldCheck
};

export const ProcessSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % processSteps.length);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + processSteps.length) % processSteps.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % processSteps.length);
  };

  return (
    <section className="relative py-20 bg-gray-50 overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-100 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-100 rounded-full blur-3xl"></div>
      </div>

      {/* Decorative shapes */}
      <div className="absolute top-10 left-10 w-20 h-20 border-4 border-orange-200 rounded-lg rotate-12 opacity-20"></div>
      <div className="absolute bottom-20 right-20 w-16 h-16 border-4 border-orange-200 rounded-full opacity-20"></div>
      <div className="absolute top-1/3 right-1/4 w-12 h-12 bg-orange-100 rounded-lg rotate-45 opacity-20"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block bg-primary text-white px-6 py-2 rounded-full text-sm font-bold mb-4">
            HOW IT WORKS
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Simple Process</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Three easy steps to protect your brand and secure your business identity
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Desktop view - all steps with connecting lines */}
          <div className="hidden md:block relative">
            {/* Connecting line */}
            <div className="absolute top-24 left-0 right-0 h-1 bg-orange-200 -z-10 mx-20"></div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {processSteps.map((step, index) => {
                const Icon = iconMap[step.icon];
                return (
                  <div key={step.id} className="relative group h-full">
                    {/* Card with fixed height */}
                    <div className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-transparent hover:border-primary/20 h-full flex flex-col">
                      {/* Number badge */}
                      <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-br from-orange-600 to-orange-500 text-white rounded-full flex items-center justify-center font-bold text-lg shadow-lg group-hover:scale-110 transition-transform duration-300">
                        {index + 1}
                      </div>
                      
                      {/* Icon */}
                      <div className="bg-gradient-to-br from-orange-100 to-orange-50 w-20 h-20 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-md">
                        <Icon className="text-primary group-hover:scale-110 transition-transform duration-300" size={36} />
                      </div>
                      
                      {/* Content - flex-1 to push content evenly */}
                      <div className="text-center flex-1 flex flex-col">
                        <div className="text-xs font-bold text-primary mb-2 uppercase tracking-wider">STEP {index + 1}</div>
                        <h3 className="text-xl font-bold text-gray-900 mb-4 min-h-[56px] flex items-center justify-center">{step.title}</h3>
                        <p className="text-base text-gray-600 leading-relaxed flex-1">{step.description}</p>
                        
                        {/* Arrow indicator */}
                        {index < processSteps.length - 1 && (
                          <div className="hidden xl:block absolute -right-12 top-1/2 -translate-y-1/2 text-primary">
                            <ArrowRight size={24} className="animate-pulse" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile view - slider */}
          <div className="md:hidden relative">
            <div className="bg-white rounded-2xl p-8 shadow-2xl border-2 border-primary/10">
              {(() => {
                const Icon = iconMap[processSteps[currentIndex].icon];
                return (
                  <>
                    {/* Number badge */}
                    <div className="absolute -top-4 -right-4 w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center font-bold text-xl shadow-lg">
                      {currentIndex + 1}
                    </div>
                    
                    <div className="bg-orange-100 w-24 h-24 rounded-2xl flex items-center justify-center mb-6 mx-auto shadow-md">
                      <Icon className="text-primary" size={44} />
                    </div>
                    <div className="text-center">
                      <div className="text-xs font-bold text-primary mb-2 uppercase tracking-wider">STEP {currentIndex + 1}</div>
                      <h3 className="text-xl font-bold text-gray-900 mb-4">{processSteps[currentIndex].title}</h3>
                      <p className="text-base text-gray-600 leading-relaxed">{processSteps[currentIndex].description}</p>
                    </div>
                  </>
                );
              })()}
            </div>

            <button
              onClick={goToPrevious}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white hover:bg-gray-100 p-3 rounded-full shadow-lg transition-all border-2 border-gray-200"
            >
              <ChevronLeft size={20} className="text-gray-700" />
            </button>
            
            <button
              onClick={goToNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white hover:bg-gray-100 p-3 rounded-full shadow-lg transition-all border-2 border-gray-200"
            >
              <ChevronRight size={20} className="text-gray-700" />
            </button>

            <div className="flex justify-center gap-2 mt-8">
              {processSteps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`h-2 rounded-full transition-all ${
                    index === currentIndex ? 'bg-primary w-8' : 'bg-gray-300 w-2'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};